import { formatDateLongMonthWithMeridiemNoSeconds as formatDate } from '~/services/TimeService';

import type { ScheduleHistoryDiffField, ScheduleHistorySchedule } from './campaignScheduleHistoryDiff';

// `availability` is "Book By Dates", not "Purchase" — the actual customer purchase gate is
// `online_purchase` ("Available Offline").
const SCHEDULE_TYPE_LABELS: Record<string, string> = {
  availability: 'Book By Dates',
  online_purchase: 'Available Offline',
  list_visibility: 'List Visibility',
  lux_plus: 'LuxPlus+',
};

const ACTION_LABELS: Record<string, string> = {
  INSERT: 'Created',
  UPDATE: 'Updated',
  DELETE: 'Deleted',
};

const DATE_DIFF_FIELDS = new Set(['start', 'end']);

const DIFF_FIELD_LABELS: Record<string, string> = {
  start: 'Start',
  end: 'End',
  marketing_channels: 'Marketing channels',
  restrict_purchase_to_lux_plus_members: 'Restrict to LuxPlus+ members',
};

export function getScheduleTypeLabel(type: string): string {
  return SCHEDULE_TYPE_LABELS[type] ?? type;
}

export function getActionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function getScheduleLabel(schedule: ScheduleHistorySchedule): string {
  return `${getScheduleTypeLabel(schedule.type)} · ${schedule.region} · ${schedule.brand}`;
}

// Summarises a merged bulk-change entry's full scope instead of just its first schedule; every
// schedule in a merged entry shares the same type, so only brand/region needs summarising.
export function getScheduleGroupLabel(schedules: Array<ScheduleHistorySchedule>): string {
  if (schedules.length === 0) {
    return '';
  }

  const [first] = schedules;
  const brands = new Set(schedules.map((schedule) => schedule.brand));
  const regions = new Set(schedules.map((schedule) => schedule.region));
  const brandPart = brands.size === 1 ? first.brand : `${brands.size} brands`;
  const regionPart = regions.size === 1 ? first.region : `${regions.size} regions`;

  return `${getScheduleTypeLabel(first.type)} · ${regionPart} · ${brandPart}`;
}

export function formatDiffValue(diff: ScheduleHistoryDiffField, value: string | null): string {
  if (!value) {
    return '—';
  }

  return DATE_DIFF_FIELDS.has(diff.field) ? formatDate(value) : value;
}

export function getDiffFieldLabel(field: string): string {
  return DIFF_FIELD_LABELS[field] ?? field;
}
